/**
 * Firebase client singleton service.
 *
 * Initializes and provides access to Firebase Auth and Firestore services.
 */

import { initializeApp } from 'firebase/app';
import type { FirebaseApp, FirebaseOptions } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
} from 'firebase/auth';
import type { Auth, UserCredential, User } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';

import { AUTH_ERRORS } from '../../constants/errorMessages';
import { AUTH } from '../../constants/timings';

/**
 * Check if Firebase is configured (all required environment variables present).
 * Returns true if Firebase can be used, false otherwise.
 */
function isFirebaseConfigured(): boolean {
  const requiredVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_APP_ID',
  ] as const;

  for (const varName of requiredVars) {
    const value = import.meta.env[varName];
    if (!value || value === 'undefined' || value === '') {
      return false;
    }
  }
  return true;
}

// Check if Firebase is configured (optional for local development)
const FIREBASE_ENABLED = isFirebaseConfigured();

if (!FIREBASE_ENABLED) {
  console.warn(
    '[Firebase] Authentication disabled - missing environment variables. ' +
      'To enable Firebase auth, add VITE_FIREBASE_* variables to .env file. ' +
      'See .env.example for required variables.'
  );
}

/**
 * Firebase configuration from environment variables.
 * Only created if Firebase is enabled.
 */
const firebaseConfig: FirebaseOptions | null = FIREBASE_ENABLED
  ? {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    }
  : null;

/** Export whether Firebase is enabled */
export const isFirebaseEnabled = FIREBASE_ENABLED;

/**
 * Firebase client singleton class.
 *
 * Provides centralized access to Firebase services with lazy initialization.
 */
class FirebaseClient {
  private static instance: FirebaseClient;
  private app: FirebaseApp | null = null;
  private authInstance: Auth | null = null;
  private firestoreInstance: Firestore | null = null;
  private googleProvider: GoogleAuthProvider | null = null;
  private facebookProvider: FacebookAuthProvider | null = null;

  /**
   * Private constructor to prevent direct instantiation.
   */
  private constructor() {
    this.initialize();
  }

  /**
   * Get singleton instance of FirebaseClient.
   *
   * @returns FirebaseClient singleton instance
   */
  public static getInstance(): FirebaseClient {
    if (!FirebaseClient.instance) {
      FirebaseClient.instance = new FirebaseClient();
    }
    return FirebaseClient.instance;
  }

  /**
   * Initialize Firebase app and services.
   * Does nothing if Firebase is not configured.
   */
  private initialize(): void {
    if (this.app || !FIREBASE_ENABLED || !firebaseConfig) {
      return; // Already initialized or Firebase disabled
    }

    // Initialize Firebase app
    this.app = initializeApp(firebaseConfig);

    // Initialize Auth
    this.authInstance = getAuth(this.app);

    // Initialize Firestore
    this.firestoreInstance = getFirestore(this.app);

    // Initialize auth providers
    this.googleProvider = new GoogleAuthProvider();
    this.facebookProvider = new FacebookAuthProvider();
  }

  /**
   * Check if Firebase is available.
   */
  public isAvailable(): boolean {
    return FIREBASE_ENABLED && this.app !== null;
  }

  /**
   * Get Firebase Auth instance.
   *
   * @returns Auth instance or null if Firebase is disabled
   */
  public getAuth(): Auth | null {
    if (!FIREBASE_ENABLED) {
      return null;
    }
    if (!this.authInstance) {
      throw new Error(AUTH_ERRORS.FIREBASE_AUTH_NOT_INITIALIZED);
    }
    return this.authInstance;
  }

  /**
   * Get Firestore instance.
   *
   * @returns Firestore instance or null if Firebase is disabled
   */
  public getFirestore(): Firestore | null {
    if (!FIREBASE_ENABLED) {
      return null;
    }
    if (!this.firestoreInstance) {
      throw new Error(AUTH_ERRORS.FIRESTORE_NOT_INITIALIZED);
    }
    return this.firestoreInstance;
  }

  /**
   * Sign in with Google popup.
   *
   * @returns Promise with user credential or null if Firebase is disabled
   */
  public async signInWithGoogle(): Promise<UserCredential | null> {
    if (!FIREBASE_ENABLED) {
      console.warn('[Firebase] Authentication disabled - cannot sign in with Google');
      return null;
    }
    if (!this.googleProvider || !this.authInstance) {
      throw new Error(AUTH_ERRORS.GOOGLE_PROVIDER_NOT_INITIALIZED);
    }
    return signInWithPopup(this.authInstance, this.googleProvider);
  }

  /**
   * Sign in with Google using ID token (for Electron desktop app).
   * This method is used when the OAuth flow is handled externally (e.g., by Electron).
   *
   * @param idToken - Google ID token obtained from OAuth flow
   * @param accessToken - Optional Google access token
   * @returns Promise with user credential or null if Firebase is disabled
   */
  public async signInWithGoogleIdToken(
    idToken: string,
    accessToken?: string
  ): Promise<UserCredential | null> {
    if (!FIREBASE_ENABLED) {
      console.warn('[Firebase] Authentication disabled - cannot sign in with Google');
      return null;
    }
    if (!this.authInstance) {
      throw new Error(AUTH_ERRORS.FIREBASE_AUTH_NOT_INITIALIZED);
    }

    // Create credential from ID token
    const credential = GoogleAuthProvider.credential(idToken, accessToken);
    return signInWithCredential(this.authInstance, credential);
  }

  /**
   * Sign in with Facebook popup.
   *
   * @returns Promise with user credential or null if Firebase is disabled
   */
  public async signInWithFacebook(): Promise<UserCredential | null> {
    if (!FIREBASE_ENABLED) {
      console.warn('[Firebase] Authentication disabled - cannot sign in with Facebook');
      return null;
    }
    if (!this.facebookProvider || !this.authInstance) {
      throw new Error(AUTH_ERRORS.FACEBOOK_PROVIDER_NOT_INITIALIZED);
    }
    return signInWithPopup(this.authInstance, this.facebookProvider);
  }

  /**
   * Sign in with email and password.
   *
   * @param email - User email
   * @param password - User password
   * @returns Promise with user credential or null if Firebase is disabled
   */
  public async signInWithEmail(email: string, password: string): Promise<UserCredential | null> {
    if (!FIREBASE_ENABLED) {
      console.warn('[Firebase] Authentication disabled - cannot sign in with email');
      return null;
    }
    if (!this.authInstance) {
      throw new Error(AUTH_ERRORS.FIREBASE_AUTH_NOT_INITIALIZED);
    }
    return signInWithEmailAndPassword(this.authInstance, email, password);
  }

  /**
   * Create new user with email and password.
   *
   * @param email - User email
   * @param password - User password
   * @param displayName - Optional display name
   * @returns Promise with user credential or null if Firebase is disabled
   */
  public async signUpWithEmail(
    email: string,
    password: string,
    displayName?: string
  ): Promise<UserCredential | null> {
    if (!FIREBASE_ENABLED) {
      console.warn('[Firebase] Authentication disabled - cannot sign up with email');
      return null;
    }
    if (!this.authInstance) {
      throw new Error(AUTH_ERRORS.FIREBASE_AUTH_NOT_INITIALIZED);
    }

    const userCredential = await createUserWithEmailAndPassword(this.authInstance, email, password);

    // Update display name if provided
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }

    return userCredential;
  }

  /**
   * Sign out current user.
   *
   * @returns Promise that resolves when sign-out completes
   */
  public async signOut(): Promise<void> {
    if (!FIREBASE_ENABLED || !this.authInstance) {
      return; // No-op if Firebase is disabled
    }
    return firebaseSignOut(this.authInstance);
  }

  /**
   * Get current authenticated user.
   *
   * @returns Current user or null
   */
  public getCurrentUser(): User | null {
    if (!FIREBASE_ENABLED) {
      return null;
    }
    return this.authInstance?.currentUser ?? null;
  }

  /**
   * Get Firebase ID token for current user.
   *
   * @returns Promise with ID token or null
   */
  public async getIdToken(): Promise<string | null> {
    if (!FIREBASE_ENABLED) {
      return null;
    }
    const user = this.getCurrentUser();
    if (!user) {
      return null;
    }
    // Proactively force-refresh when the cached token is at/near expiry.
    //
    // user.getIdToken(false) returns the cached token while it is still valid by
    // the client clock, with only a tiny buffer. Firebase's background proactive
    // refresh normally keeps the cached token far from expiry, but in Electron
    // that timer is unreliable (Chromium throttles background timers, machine
    // sleep stops them) and balance/jackpot fetches fire on app startup before
    // any refresh runs. A near-expiry token then dies in flight (network latency
    // + client/server clock skew) and the server rejects it as
    // "auth/token-expired" (observed: exp only 1-2s in the past). Refreshing
    // within TOKEN_REFRESH_SAFETY_WINDOW_MS of expiry removes the race entirely.
    try {
      const result = await user.getIdTokenResult();
      const expiresAtMs = new Date(result.expirationTime).getTime();
      const needsRefresh =
        !Number.isFinite(expiresAtMs) ||
        Date.now() >= expiresAtMs - AUTH.TOKEN_REFRESH_SAFETY_WINDOW_MS;
      return await user.getIdToken(needsRefresh);
    } catch {
      // getIdTokenResult() already refreshes a fully-expired token; if anything
      // goes wrong inspecting expiry, fall back to the SDK's default behavior.
      return user.getIdToken();
    }
  }
}

// Export singleton instance
export const firebaseClient = FirebaseClient.getInstance();
